import React from 'react'

/**
 * OfflineScopeSheet - one-screen explainer of what works without
 * internet in Tutor Moz, what doesn't, and why packs cost credits.
 *
 * Slice 5 + Slice 6 (UI_UX_IMPLEMENTATION_PLAN_MAY_2026.md,
 * 2026-05-12). Surfaces from:
 *   - "?" icon in `/profile → Offline` page header
 *   - "?" icon on each `OfflinePackCard` cost line
 *   - In-flow "Saber mais" link when the student loses connection
 *     mid-practice (offline acknowledgement)
 *
 * Static content. No data fetching. Bottom-sheet style on mobile -
 * tappable backdrop + a single "Fechar" button. Mobile-first per
 * project memory.
 */

interface Props {
  /** Sheet visibility. Parent owns the state. */
  open:    boolean
  onClose: () => void
}

const WORKS_OFFLINE = [
  'Praticar com pacotes descarregados',
  'Ver as resoluções dos pacotes',
  'Submeter respostas (sincronizam quando voltares online)',
]
const NEEDS_INTERNET = [
  'Tutor (chat com IA)',
  'Descarregar novos pacotes',
  'Rankings e missão diária',
  'Notificações e novidades',
]

export function OfflineScopeSheet({ open, onClose }: Props) {
  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="O que funciona sem internet"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            O que funciona sem internet
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Para quando a tua ligação falhar.
          </p>
        </div>

        <div className="px-5 py-4 space-y-4">
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-green-700 mb-2">
              Funciona offline
            </h3>
            <ul className="space-y-1.5">
              {WORKS_OFFLINE.map(item => (
                <li key={item} className="text-sm text-gray-800 flex items-start gap-2">
                  <span aria-hidden className="text-green-600 mt-0.5">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Precisa de internet
            </h3>
            <ul className="space-y-1.5">
              {NEEDS_INTERNET.map(item => (
                <li key={item} className="text-sm text-gray-800 flex items-start gap-2">
                  <span aria-hidden className="text-gray-400 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="pt-2 border-t border-gray-100">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Porque os pacotes custam créditos
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed">
              Cada pacote usa créditos do teu limite diário. É a forma
              de garantir que o que descarregas é mesmo o que vais
              estudar - sem isso, qualquer pessoa podia descarregar
              tudo de uma vez sem realmente praticar.
            </p>
            <p className="text-xs text-gray-600 leading-relaxed mt-2">
              Os créditos renovam todos os dias. Uma vez descarregado,
              o pacote não gasta mais nada quando o usares offline.
            </p>
          </section>
        </div>

        <div className="px-5 py-3 border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full bg-indigo-600 text-white text-sm font-semibold py-3 rounded-lg hover:bg-indigo-700"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}
